import React from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useMarketBySlug } from '../hooks/useMarketCompassApi';
import { useAuth } from '../hooks/useAuth';

interface NonActiveMarketGuardProps {
  children: React.ReactNode;
}

const NonActiveMarketGuard: React.FC<NonActiveMarketGuardProps> = ({ children }) => {
  const { continentName, countryName, marketName } = useParams<{ 
    continentName: string;
    countryName: string;
    marketName: string;
  }>();
  const { isAuthenticated, user } = useAuth();
  const { 
    data: market, 
    isLoading: loadingMarket, 
    error: marketError 
  } = useMarketBySlug(continentName, countryName, marketName);

  // Check if user has valid domain
  const hasValidDomain = (email: string | undefined): boolean => {
    if (!email) return false;
    const emailDomain = email.toLowerCase().split('@')[1];
    return emailDomain === 'horwathhtl.com' || emailDomain === 'barretthtl.com';
  };

  // Show loading while fetching market data
  if (loadingMarket) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center'
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading market details...
        </Typography>
      </Box>
    );
  }

  // Handle errors in fetching market data
  if (marketError || !market) {
    // Allow the component to handle the error (market not found, etc.)
    return <>{children}</>;
  }

  // If market is active, allow access
  if (market.isActive) {
    return <>{children}</>;
  }

  // Market is not active - check authentication and domain
  if (!isAuthenticated || !hasValidDomain(user?.email)) {
    // Return 401 Unauthorized for non-active markets without proper authentication
    // We'll use a custom 401 response by returning an error component
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center',
          p: 4
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          401 - Unauthorized
        </Typography>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
          This market is not currently active for public access. Access is restricted to authenticated users 
          with @horwathhtl.com or @barretthtl.com email addresses.
        </Typography>
        {!isAuthenticated && (
          <Typography variant="body2" sx={{ mt: 2, color: 'primary.main' }}>
            Please log in with an authorized email address to access this market.
          </Typography>
        )}
        {isAuthenticated && !hasValidDomain(user?.email) && (
          <Typography variant="body2" sx={{ mt: 2, color: 'primary.main' }}>
            Your current email domain ({user?.email?.split('@')[1]}) is not authorized to access this market.
          </Typography>
        )}
      </Box>
    );
  }

  // User is authenticated with valid domain, allow access to non-active market
  return <>{children}</>;
};

export default NonActiveMarketGuard;